import { Button, CircularProgress, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useQueryClient } from '@tanstack/react-query';

interface Props {
  /**
   * Query key prefixes to invalidate. Prefixes rather than exact keys, so a view does not have to know
   * which page or which filter combination is currently cached — invalidating `['items']` catches
   * `['items', 25, {provider: […]}]` and every other variant.
   */
  queryKeys: string[];
  /** True while any of those queries is already in flight, so the control shows what it is doing. */
  busy?: boolean;
  label?: string;
}

/**
 * Re-fetch what is on screen, without losing where you are.
 *
 * VMS shows a pipeline that moves on its own: packages arrive from the provider, ingest and validation
 * run asynchronously, an export completes some seconds after it is requested, and colleagues edit the
 * same catalogue. So every list and every detail view goes stale while somebody is looking at it, and
 * until now the only remedy was a browser reload — which also discards the filters, the search text,
 * the open tab and the scroll position. People stopped reloading and started distrusting the screen
 * instead.
 *
 * Invalidating rather than refetching directly: React Query then refreshes exactly the queries that
 * are actually mounted and leaves the rest alone, and the cached data stays visible while the new data
 * arrives, so the table does not blank out and reflow under the pointer.
 */
export default function RefreshButton({
  queryKeys,
  busy,
  label = 'Refresh',
}: Props) {
  const queryClient = useQueryClient();

  const refresh = () => {
    for (const key of queryKeys) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
  };

  return (
    <Tooltip title="Re-fetch this view. Filters, search and scroll position are kept.">
      <span>
        <Button
          size="small"
          variant="outlined"
          onClick={refresh}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} /> : <RefreshIcon />}
        >
          {busy ? 'Refreshing…' : label}
        </Button>
      </span>
    </Tooltip>
  );
}
